import {ChangeDetectorRef, Component, ElementRef, forwardRef, Input, OnDestroy, OnInit} from "@angular/core";
import {DomSanitizer, SafeHtml, SafeResourceUrl} from "@angular/platform-browser";
import {TranslateService} from "@ngx-translate/core";
import {NG_VALUE_ACCESSOR} from "@angular/forms";
import {Subscription} from "rxjs";
import {FUIResponsiveService} from "../../page/responsive.service";

export enum CanvasType {
    CHECKERED,
    DARK,
    LIGHT
}

@Component({
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FUIImageComponent),
            multi: true
        }
    ],
    selector: "fui-image",
    styleUrls: ["./image.component.scss"],
    templateUrl: "./image.component.html"
})
export class FUIImageComponent implements OnInit, OnDestroy {
    CanvasType = CanvasType;
    @Input() allowSvg: boolean = false;
    @Input() canvasType: CanvasType = CanvasType.CHECKERED;
    imageUrl: SafeResourceUrl;
    imageSvg: SafeHtml;
    showCanvas: boolean = false;
    destroyed: boolean = false;

    private subscriptions: Subscription = new Subscription();

    private static isSvg(base64: string): boolean {
        if (base64 == undefined || base64.length == 0) {
            return false;
        }
        const bytes: string = atob(base64.substring(0, 12));
        return !!bytes.match(/^<svg\b/) || !!bytes.match(/^<\?xml\b/);
    }

    private _imageBase64: string;

    constructor(protected sanitizer: DomSanitizer,
                protected changeDetectorRef: ChangeDetectorRef,
                protected translateService: TranslateService,
                protected responsiveService: FUIResponsiveService,
                protected element: ElementRef) {
    }

    get imageBase64(): any {
        return this._imageBase64;
    }

    @Input()
    set imageBase64(imageBase64: any) {
        this._imageBase64 = imageBase64;

        const isSvg: boolean = FUIImageComponent.isSvg(imageBase64);
        this.imageUrl = isSvg ? undefined : this.sanitizer.bypassSecurityTrustResourceUrl("data:image;base64," + imageBase64);
        this.imageSvg = isSvg && this.allowSvg ? this.sanitizer.bypassSecurityTrustHtml(atob(imageBase64)) : undefined;
        this.showCanvas = false;
        this.changeDetectorRef.detectChanges();
        setTimeout(() => {
            if (!this.destroyed) {
                this.resizeSvg();
                this.showCanvas = true;
                this.changeDetectorRef.detectChanges();
            }
        }, 500);
    }

    private _imageOpacity: number = 1.0;

    get imageOpacity(): number {
        return this._imageOpacity;
    }

    @Input()
    set imageOpacity(imageOpacity: number) {
        if (imageOpacity == undefined) {
            imageOpacity = 1.0;
        }
        this._imageOpacity = imageOpacity;
        this.element.nativeElement.style.setProperty("--image-opacity", imageOpacity.toString());
    }

    ngOnInit(): void {
        this.subscriptions.add(this.responsiveService.window$.subscribe((window) => this.resizeSvg()));
        this.subscriptions.add(this.translateService.get("fui.image-control.label.loading").subscribe(translation => this.element.nativeElement.style.setProperty("--loading-message", "\"" + translation + "\"")));
        this.resizeSvg();
    }

    public ngOnDestroy(): void {
        this.destroyed = true;
        this.subscriptions.unsubscribe();
    }

    private resizeSvg(): void {
        this.element.nativeElement.style.removeProperty("--svg-width");
        this.element.nativeElement.style.removeProperty("--svg-height");
        if (this.element) {
            const svg: SVGMarkerElement = this.element.nativeElement.querySelector("svg");
            if (svg) {
                const elementRect: DOMRect = this.element.nativeElement.getBoundingClientRect();
                const viewBox: SVGRect = svg.viewBox.baseVal;
                const wScale: number = elementRect.width / viewBox.width;
                const hScale: number = elementRect.height / viewBox.height;
                const scale: number = Math.min(wScale, hScale);
                this.element.nativeElement.style.setProperty("--svg-width", (scale * viewBox.width).toString() + "px");
                this.element.nativeElement.style.setProperty("--svg-height", (scale * viewBox.height).toString() + "px");
            }
        }
    }
}
